import { Button, Stack, Typography } from '@mui/material';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BasicDialog } from '../../components/common/BasicDialog';
import './RecurringEventScopeModalProvider.scss';
import { useTranslation } from 'react-i18next';

export type RecurringEventScope = 'thisEvent' | 'allEvents' | 'cancel';

interface RecurringEventScopeModalOptions {
  title?: string;
  message?: string;
  thisEventLabel?: string;
  allEventsLabel?: string;
  cancelLabel?: string;
}

interface RecurringEventScopeModalContextValue {
  requestRecurringEventScope: (
    options?: RecurringEventScopeModalOptions
  ) => Promise<RecurringEventScope>;
}

const fallbackOptions: Required<RecurringEventScopeModalOptions> = {
  title: 'Recurring Event',
  message: 'This is a recurring event. Which events do you want to apply this change to?',
  thisEventLabel: 'This Event',
  allEventsLabel: 'All Events',
  cancelLabel: 'Cancel',
};

const RecurringEventScopeModalContext = createContext<RecurringEventScopeModalContextValue>({
  requestRecurringEventScope: async () => 'cancel',
});

export const useRecurringEventScopeModal = () => useContext(RecurringEventScopeModalContext);

export const RecurringEventScopeModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const defaultOptions = useMemo<Required<RecurringEventScopeModalOptions>>(
    () => ({
      title: t('calendar.recurringScope.title'),
      message: t('calendar.recurringScope.defaultMessage'),
      thisEventLabel: t('calendar.recurringScope.thisEvent'),
      allEventsLabel: t('calendar.recurringScope.allEvents'),
      cancelLabel: t('calendar.common.cancel'),
    }),
    [t]
  );
  const resolverRef = useRef<((value: RecurringEventScope) => void) | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] =
    useState<Required<RecurringEventScopeModalOptions>>(fallbackOptions);

  useEffect(() => {
    if (!isOpen) {
      setOptions(defaultOptions);
    }
  }, [defaultOptions, isOpen]);

  const handleResolve = (value: RecurringEventScope) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setIsOpen(false);
    resolver?.(value);
  };

  const requestRecurringEventScope = (nextOptions: RecurringEventScopeModalOptions = {}) => {
    if (resolverRef.current) {
      resolverRef.current('cancel');
      resolverRef.current = null;
    }

    setOptions({
      ...defaultOptions,
      ...nextOptions,
    });
    setIsOpen(true);

    return new Promise<RecurringEventScope>((resolve) => {
      resolverRef.current = resolve;
    });
  };

  const contextValue = useMemo(
    () => ({ requestRecurringEventScope }),
    [requestRecurringEventScope]
  );

  return (
    <RecurringEventScopeModalContext.Provider value={contextValue}>
      {children}
      <BasicDialog
        visible={isOpen}
        title={options.title}
        ctaString={options.thisEventLabel}
        onYes={() => handleResolve('thisEvent')}
        onClose={() => handleResolve('cancel')}
        className="recurring-event-scope-modal"
        onKeyDown={(event) => {
          if (event.key !== 'Enter') {
            return;
          }

          event.preventDefault();

          if (event.shiftKey) {
            handleResolve('allEvents');
            return;
          }

          handleResolve('thisEvent');
        }}
        actions={
          <Stack
            direction="row"
            gap={1}
            useFlexGap
            flexWrap="wrap"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button onClick={() => handleResolve('cancel')}>
              <Typography variant="body2" className="basic-dialog__actions__cta-text">
                {options.cancelLabel}
              </Typography>
            </Button>
            <Stack direction="row" gap={1} useFlexGap flexWrap="wrap">
              <Button
                onClick={() => handleResolve('allEvents')}
                variant="outlined"
                className="recurring-event-scope-modal__secondary-button"
              >
                <Typography variant="body2" className="basic-dialog__actions__cta-text">
                  {options.allEventsLabel}
                </Typography>
              </Button>
              <Button
                onClick={() => handleResolve('thisEvent')}
                className="basic-dialog__actions__cta-button"
              >
                <Typography
                  variant="body2"
                  className="basic-dialog__actions__cta-text basic-dialog__actions__cta-text--bold-white"
                >
                  {options.thisEventLabel}
                </Typography>
              </Button>
            </Stack>
          </Stack>
        }
      >
        <Typography variant="body2" className="basic-dialog__content-text">
          {options.message}
        </Typography>
      </BasicDialog>
    </RecurringEventScopeModalContext.Provider>
  );
};
